import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Card, CardBody, Col, Container, FormGroup, Input, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { MetaTags } from "react-meta-tags"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import { AppConfiguration } from "config"
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton"
import OffsetPagination from "components/Common/OffsetPagination"
import CustomProspectFilters from "components/Common/CustomFilters"
import ShowAppliedFilters from "components/Common/CustomFilters/ShowAppliedFilters"
import CustomExportCSVButton from "components/Common/CustomExportCSVButton"
import { customSortCaret } from "helpers/bootstrap_table_helper"
import { copyToClipboard } from "helpers/Integrations/req_res_text_area_helper"
import { exportAdminBulkEditJobs, getAdminBulkEditJobs } from "store/admin/admin-bulk-edit-jobs/actions"
import { postAdminUserLogin } from "store/admin/user-login/actions"
import { BULK_EDIT_JOBS_FILTER_OBJECTS, bulkEditJobsColumns, mapBulkEditJobsFilters } from "./admin_bulk_edit_jobs_helper"
import { getActiveFilterItems, onClickOpenFilterModal, onClickRemoveFilter, onCloseFilterModal } from "./AdminBulkEditJobsFilterUtils"

const DEFAULT_SORT_COLUMN = "created_at"
const DEFAULT_SORT_DIRECTION = "desc"
// The paired API endpoint (POST /api/v1/admin_bulk_edit_jobs/export) only
// counts matching rows today -- it doesn't create an export log, enqueue a
// job, or generate a file, so this button would otherwise show a false
// "queued" success. Flip this back on once that backend work lands.
// Stable reference: a new object here would make BootstrapTable think its
// remote config changed on every render and re-fire onTableChange, looping.
const REMOTE_SORT_CONFIG = { sort: true }
const DEFAULT_FILTER_ITEMS = Object.values(BULK_EDIT_JOBS_FILTER_OBJECTS)
const EMPTY_MODAL_STATE = { isOpen: false, type: null, title: "", job: null, content: "" }

const formatModalValue = (value) => {
    if (value === null || value === undefined || value === "") return "--"
    if (typeof value === "string") return value
    return JSON.stringify(value, null, 2)
}

const AdminBulkMigration = () => {
    const dispatch = useDispatch()
    const history = useHistory()

    const { bulk_edit_jobs, loading, error, page, total_pages, exporting } = useSelector((state) => ({
        bulk_edit_jobs: state.AdminBulkEditJobs.bulk_edit_jobs,
        loading: state.AdminBulkEditJobs.loading,
        error: state.AdminBulkEditJobs.error,
        page: state.AdminBulkEditJobs.page,
        total_pages: state.AdminBulkEditJobs.total_pages,
        exporting: state.AdminBulkEditJobs.exporting,
    }))

    const [searchValue, setSearchValue] = useState("")
    const [isActiveFilterModal, setIsActiveFilterModal] = useState(false)
    const [filterItems, setFilterItems] = useState(DEFAULT_FILTER_ITEMS)
    const [detailsModal, setDetailsModal] = useState(EMPTY_MODAL_STATE)
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN)
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION)

    const { filterCount, activeFilterItems } = useMemo(() => getActiveFilterItems(filterItems), [filterItems])

    const filtersPayload = useMemo(() => {
        const filters = mapBulkEditJobsFilters(activeFilterItems)
        if (searchValue) filters.search = searchValue
        return filters
    }, [activeFilterItems, searchValue])

    const fetchBulkEditJobsPage = useCallback((pageNumber = 1, filters = filtersPayload, sortCol = sortColumn, sortDir = sortDirection) => {
        const queryParams = { page: pageNumber, sort_column: sortCol, sort_direction: sortDir }
        if (filters && Object.keys(filters).length) {
            queryParams.filters = JSON.stringify(filters)
        }
        dispatch(getAdminBulkEditJobs({ queryParams }))
    }, [dispatch, filtersPayload, sortColumn, sortDirection])

    useEffect(() => {
        // Intentionally mount-only: fetches with the initial default filters/sort.
        // Every later filter/sort/search change calls fetchBulkEditJobsPage with its
        // own explicit args (see below), so this effect never needs to re-fire or
        // depend on fetchBulkEditJobsPage's identity.
        fetchBulkEditJobsPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSearch = (value) => {
        setSearchValue(value)
        const filters = mapBulkEditJobsFilters(activeFilterItems)
        if (value) filters.search = value
        fetchBulkEditJobsPage(1, filters)
    }

    const onSubmitFilterModal = (values) => {
        setFilterItems(values)
        const { activeFilterItems: submittedActiveItems } = getActiveFilterItems(values)
        const filters = mapBulkEditJobsFilters(submittedActiveItems)
        if (searchValue) filters.search = searchValue
        fetchBulkEditJobsPage(1, filters)
        onCloseFilterModal(setIsActiveFilterModal)
    }

    const onRemoveFilter = (fieldName) => {
        onClickRemoveFilter(fieldName, setFilterItems, (updatedActiveItems) => {
            const filters = mapBulkEditJobsFilters(updatedActiveItems)
            if (searchValue) filters.search = searchValue
            fetchBulkEditJobsPage(1, filters)
        })
    }

    const onTableChange = (type, { sortField, sortOrder }) => {
        if (type !== "sort") {
            return
        }
        // The table remounts behind the loading skeleton on every fetch and
        // re-fires onTableChange reporting the current sort; ignore that
        // redundant call so it doesn't trigger another fetch (infinite loop).
        if (sortField === sortColumn && sortOrder === sortDirection) {
            return
        }
        setSortColumn(sortField)
        setSortDirection(sortOrder)
        fetchBulkEditJobsPage(1, filtersPayload, sortField, sortOrder)
    }

    const openJobDetailsModal = useCallback((job) => {
        setDetailsModal({ isOpen: true, type: "job", title: "Job Details", job, content: "" })
    }, [])

    const openMessageModal = useCallback((title, content) => {
        setDetailsModal({ isOpen: true, type: "message", title, job: null, content })
    }, [])

    const onClickAccountLogin = useCallback((accountId) => {
        if (!accountId) return
        dispatch(postAdminUserLogin({ user: { account: accountId }, history }))
    }, [dispatch, history])

    const closeDetailsModal = () => setDetailsModal(EMPTY_MODAL_STATE)

    const columns = useMemo(() => bulkEditJobsColumns({ openJobDetailsModal, openMessageModal, onClickAccountLogin }), [openJobDetailsModal, openMessageModal, onClickAccountLogin])

    const onClickExport = () => {
        if (exporting) return
        const queryParams = { sort_column: sortColumn, sort_direction: sortDirection }
        if (filtersPayload && Object.keys(filtersPayload).length) {
            queryParams.filters = JSON.stringify(filtersPayload)
        }
        dispatch(exportAdminBulkEditJobs({ queryParams }))
    }

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>Bulk Edit Logs | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <CustomProspectFilters
                    isActive={isActiveFilterModal}
                    filterItems={filterItems}
                    onSubmitModalState={onSubmitFilterModal}
                    onClickModalClose={() => onCloseFilterModal(setIsActiveFilterModal)}
                    isActiveSearchBox={false}
                />
                <Modal isOpen={detailsModal.isOpen} toggle={closeDetailsModal} centered size="md">
                    <ModalHeader toggle={closeDetailsModal}>{detailsModal.title}</ModalHeader>
                    <ModalBody>
                        {detailsModal.type === "job" ? (
                            <>
                                <div className="mb-3">
                                    <div className="text-muted font-size-12">Name</div>
                                    <div className="text-black">{formatModalValue(detailsModal.job?.job_name)}</div>
                                </div>
                                <div className="mb-3">
                                    <div className="text-muted font-size-12">Job ID</div>
                                    <div className="text-black">{formatModalValue(detailsModal.job?.jid)}</div>
                                </div>
                                <div className="mb-3">
                                    <div className="text-muted font-size-12">Request ID</div>
                                    <div className="text-black">{formatModalValue(detailsModal.job?.request_id)}</div>
                                </div>
                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <div className="text-muted font-size-12">Payload</div>
                                        <button
                                            type="button"
                                            className="text-sfblue bg-transparent border-0 p-0"
                                            onClick={() => copyToClipboard(formatModalValue(detailsModal.job?.payload))}
                                        >
                                            <i className="bx bx-copy me-1" />
                                            Copy
                                        </button>
                                    </div>
                                    <pre className="bg-light border rounded p-2 mb-0 text-black" style={{ maxHeight: "240px", overflow: "auto", whiteSpace: "pre-wrap" }}>
                                        {formatModalValue(detailsModal.job?.payload)}
                                    </pre>
                                </div>
                            </>
                        ) : (
                            <div>
                                <div className="d-flex align-items-center justify-content-end mb-1">
                                    <button
                                        type="button"
                                        className="text-sfblue bg-transparent border-0 p-0"
                                        onClick={() => copyToClipboard(formatModalValue(detailsModal.content))}
                                    >
                                        <i className="bx bx-copy me-1" />
                                        Copy
                                    </button>
                                </div>
                                <pre className="bg-light border rounded p-2 mb-0 text-black" style={{ maxHeight: "240px", overflow: "auto", whiteSpace: "pre-wrap" }}>
                                    {formatModalValue(detailsModal.content)}
                                </pre>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
                <Container fluid>
                    <div className="page-title-box pb-2 d-flex align-items-center justify-content-between">
                        <h4 className="text-capitalize text-black mb-0 font-size-18">
                            Bulk Edit Logs
                        </h4>
                    </div>
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <Row className="mb-2 d-flex align-items-center">
                                        <Col md="auto" className="d-flex align-items-center">
                                            <FormGroup className="mb-0">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="d-flex align-items-center btn btn-outline-primary"
                                                        onClick={() => onClickOpenFilterModal(setIsActiveFilterModal, setFilterItems, BULK_EDIT_JOBS_FILTER_OBJECTS)}
                                                    >
                                                        <i className="bx bx-filter font-size-18 align-middle me-2" />
                                                        Filters {filterCount !== 0 && <span className="numberCircle border-0"> {filterCount} </span>}
                                                    </button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md="2" className="d-flex align-items-center">
                                            <FormGroup className="mb-0 w-100">
                                                <div className="search-box d-flex align-items-center w-100">
                                                    <div className="position-relative w-100">
                                                        <Input
                                                            className="rounded"
                                                            placeholder="Search"
                                                            value={searchValue}
                                                            onChange={(e) => {
                                                                setSearchValue(e.target.value)
                                                                if (e.target.value === "") {
                                                                    onSearch("")
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault()
                                                                    onSearch(e.target.value)
                                                                }
                                                            }}
                                                        />
                                                        <i className="bx bx-search-alt search-icon" />
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col className="flex-nowrap d-flex align-items-center" style={{ maxHeight: "100px" }}>
                                            <ShowAppliedFilters
                                                filtered_columns={activeFilterItems}
                                                onClickRemoveFilter={onRemoveFilter}
                                            />
                                        </Col>
                                        <Col md="auto" className="d-flex gap-2 ms-auto">
                                            <CustomExportCSVButton
                                                className={`d-flex align-items-center btn btn-outline-primary${exporting ? " disabled" : ""}`}
                                                applyCustomOnClick
                                                onClick={onClickExport}
                                            >
                                                <i className="bx bx-download font-size-18 me-2" />
                                                {exporting ? "Exporting..." : "Export"}
                                            </CustomExportCSVButton>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl="12">
                                            <div className="sfTable-Container">
                                                {error ? (
                                                    <div className="text-center text-danger py-4">
                                                        Something went wrong while loading Bulk Edit Logs. Please try again.
                                                    </div>
                                                ) : loading ? (
                                                    <SfLoadingSkeleton rows={8} cols={10} />
                                                ) : (
                                                    <ToolkitProvider
                                                        keyField="id"
                                                        columns={columns}
                                                        data={bulk_edit_jobs || []}
                                                    >
                                                        {(toolkitProps) => (
                                                            <BootstrapTable
                                                                keyField="id"
                                                                responsive
                                                                bordered={false}
                                                                striped={false}
                                                                rowClasses="tr-hover"
                                                                headerClasses="sftableHeader text-black thead-h50"
                                                                classes="table align-middle table-nowrap text-black"
                                                                headerWrapperClasses="thead-light"
                                                                noDataIndication="You currently don't have any bulk edit logs"
                                                                defaultSorted={[{ dataField: sortColumn, order: sortDirection }]}
                                                                sort={{ sortCaret: customSortCaret }}
                                                                remote={REMOTE_SORT_CONFIG}
                                                                onTableChange={onTableChange}
                                                                {...toolkitProps.baseProps}
                                                            />
                                                        )}
                                                    </ToolkitProvider>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Col xl="12">
                                        <OffsetPagination
                                            currentPage={page}
                                            totalPages={total_pages}
                                            hasNextPage={page < total_pages}
                                            hasDeepOffsetPages={false}
                                            loading={loading}
                                            onPageChange={(pageNumber) => fetchBulkEditJobsPage(pageNumber)}
                                            className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                        />
                                    </Col>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AdminBulkMigration
